import { useCallback, useMemo } from "react";
import {
  Table,
  Checkbox,
  Badge,
  ActionIcon,
  Group,
  Text,
  Box,
  Stack,
  Card,
  Center,
  Loader,
  Tooltip,
} from "@mantine/core";
import { IconDownload, IconPrinter } from "@tabler/icons-react";
import { PortalSortHeader } from "./PortalSortHeader";
import {
  type PortalCaseListItem,
  type PortalCaseStatus,
} from "../../services/physicianPortal.service";
import { formatDateOnlyForDisplay, parseDateOnly } from "../../utils/dateOnly";
import { formatLabCalendarDate } from "../../utils/labTime";

interface PortalReportTableProps {
  items: PortalCaseListItem[];
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  onDownload: (item: PortalCaseListItem) => void;
  onPrint: (item: PortalCaseListItem) => void;
  onRowClick?: (id: string) => void;
  loading?: boolean;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  onSort?: (field: string) => void;
}

const getStatusColor = (status: PortalCaseStatus): string => {
  switch (status) {
    case "received_by_lab":
      return "blue";
    case "new":
      return "orange";
    case "available":
      return "teal";
    case "viewed":
    default:
      return "gray";
  }
};

const formatPortalDateOnly = (value: unknown): string => {
  const dateOnly = parseDateOnly(value);
  return dateOnly ? formatDateOnlyForDisplay(dateOnly) : "—";
};

const formatPortalReportInstant = (value: string | null): string => {
  if (!value) return "—";
  const instant = new Date(value);
  return Number.isNaN(instant.getTime()) ? "—" : formatLabCalendarDate(instant);
};

export function PortalReportTable({
  items,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onDownload,
  onPrint,
  onRowClick,
  loading = false,
  sortField,
  sortOrder,
  onSort,
}: PortalReportTableProps) {
  const availableItems = useMemo(
    () => items.filter((item) => item.reportAvailable),
    [items]
  );

  const isAllSelected = useMemo(() => {
    return (
      availableItems.length > 0 && selectedIds.length === availableItems.length
    );
  }, [availableItems, selectedIds]);

  const isSomeSelected = useMemo(() => {
    return selectedIds.length > 0 && selectedIds.length < availableItems.length;
  }, [availableItems, selectedIds]);

  const handleSelectClick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const id = event.currentTarget.getAttribute("data-id");
      if (id) {
        onToggleSelect(id);
      }
    },
    [onToggleSelect]
  );

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement | HTMLTableCellElement>) => {
      const id = event.currentTarget.getAttribute("data-id");
      if (id && onRowClick) {
        onRowClick(id);
      }
    },
    [onRowClick]
  );

  const handleDownloadClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const id = event.currentTarget.getAttribute("data-id");
      const item = items.find((i) => i.caseId === id);
      if (item) {
        onDownload(item);
      }
    },
    [items, onDownload]
  );

  const handlePrintClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const id = event.currentTarget.getAttribute("data-id");
      const item = items.find((i) => i.caseId === id);
      if (item) {
        onPrint(item);
      }
    },
    [items, onPrint]
  );

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="md" />
      </Center>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <Box>
      {/* Desktop View */}
      <Box visibleFrom="sm">
        <Table verticalSpacing="sm" highlightOnHover striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 40 }}>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isSomeSelected}
                  onChange={onToggleSelectAll}
                  aria-label="Select all reports"
                />
              </Table.Th>
              <PortalSortHeader
                field="caseNumber"
                label="Case ID"
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <PortalSortHeader
                field="patientName"
                label="Patient Name"
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <PortalSortHeader
                field="physician"
                label="Physician"
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <PortalSortHeader
                field="mrn"
                label="MRN"
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <Table.Th>DOB</Table.Th>
              <Table.Th>Status</Table.Th>
              <PortalSortHeader
                field="dateOfService"
                label="Date of Service"
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <PortalSortHeader
                field="reportDate"
                label="Report Date"
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <Table.Th style={{ textAlign: "right" }}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item) => {
              const isSelected = selectedIds.includes(item.caseId);
              return (
                <Table.Tr
                  key={item.caseId}
                  style={{
                    backgroundColor: isSelected
                      ? "var(--mantine-color-gcpBlue-0)"
                      : undefined,
                  }}
                >
                  <Table.Td>
                    {item.reportAvailable && (
                      <Checkbox
                        checked={isSelected}
                        onChange={handleSelectClick}
                        data-id={item.caseId}
                        aria-label={`Select report for ${item.patientName}`}
                      />
                    )}
                  </Table.Td>
                  <Table.Td
                    style={{
                      fontWeight: 600,
                      cursor:
                        onRowClick && item.reportAvailable
                          ? "pointer"
                          : undefined,
                    }}
                    onClick={item.reportAvailable ? handleRowClick : undefined}
                    data-id={item.caseId}
                  >
                    {item.caseNumber}
                  </Table.Td>
                  <Table.Td
                    style={{
                      cursor:
                        onRowClick && item.reportAvailable
                          ? "pointer"
                          : undefined,
                    }}
                    onClick={item.reportAvailable ? handleRowClick : undefined}
                    data-id={item.caseId}
                  >
                    {item.patientName}
                  </Table.Td>
                  <Table.Td>{item.physicianName || "—"}</Table.Td>
                  <Table.Td>{item.mrn || "—"}</Table.Td>
                  <Table.Td>{formatPortalDateOnly(item.patientDob)}</Table.Td>
                  <Table.Td>
                    <Badge
                      color={getStatusColor(item.reportStatus)}
                      variant="light"
                    >
                      {item.reportStatusLabel}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {formatPortalDateOnly(item.dateOfService)}
                  </Table.Td>
                  <Table.Td>
                    {item.reportAvailable
                      ? formatPortalReportInstant(item.reportDate)
                      : "—"}
                  </Table.Td>
                  <Table.Td>
                    {item.reportAvailable && (
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        <Tooltip label="Download Report PDF" position="top">
                          <ActionIcon
                            variant="subtle"
                            color="gcpBlue.6"
                            onClick={handleDownloadClick}
                            data-id={item.caseId}
                            aria-label="Download PDF"
                            size={44}
                          >
                            <IconDownload size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Print Report PDF" position="top">
                          <ActionIcon
                            variant="subtle"
                            color="gcpBlue.6"
                            onClick={handlePrintClick}
                            data-id={item.caseId}
                            aria-label="Print PDF"
                            size={44}
                          >
                            <IconPrinter size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Box>

      {/* Mobile View */}
      <Box hiddenFrom="sm">
        <Stack gap="sm">
          {items.map((item) => {
            const isSelected = selectedIds.includes(item.caseId);
            return (
              <Card
                key={item.caseId}
                withBorder
                padding="md"
                radius="md"
                style={{
                  backgroundColor: isSelected
                    ? "var(--mantine-color-gcpBlue-0)"
                    : "var(--mantine-color-white)",
                  borderColor: isSelected
                    ? "var(--mantine-color-gcpBlue-3)"
                    : "var(--mantine-color-gray-2)",
                }}
              >
                <Group
                  justify="space-between"
                  align="flex-start"
                  wrap="nowrap"
                  mb="xs"
                >
                  <Group gap="xs" align="center">
                    {item.reportAvailable && (
                      <Checkbox
                        checked={isSelected}
                        onChange={handleSelectClick}
                        data-id={item.caseId}
                        aria-label={`Select report for ${item.patientName}`}
                      />
                    )}
                    <Stack
                      gap={2}
                      style={{
                        cursor:
                          onRowClick && item.reportAvailable
                            ? "pointer"
                            : undefined,
                      }}
                      onClick={
                        item.reportAvailable ? handleRowClick : undefined
                      }
                      data-id={item.caseId}
                    >
                      <Text size="sm" fw={600} c="gcpBlue.7">
                        {item.caseNumber}
                      </Text>
                      <Text fw={600} size="md" style={{ lineHeight: 1.2 }}>
                        {item.patientName}
                      </Text>
                    </Stack>
                  </Group>
                  <Badge
                    color={getStatusColor(item.reportStatus)}
                    variant="light"
                  >
                    {item.reportStatusLabel}
                  </Badge>
                </Group>

                <Stack gap={4} mb="md">
                  <Group justify="space-between" gap="xs">
                    <Text size="xs" c="dimmed">
                      Physician
                    </Text>
                    <Text size="xs" fw={500}>
                      {item.physicianName || "—"}
                    </Text>
                  </Group>
                  <Group justify="space-between" gap="xs">
                    <Text size="xs" c="dimmed">
                      MRN
                    </Text>
                    <Text size="xs" fw={500}>
                      {item.mrn || "—"}
                    </Text>
                  </Group>
                  <Group justify="space-between" gap="xs">
                    <Text size="xs" c="dimmed">
                      DOB
                    </Text>
                    <Text size="xs" fw={500}>
                      {formatPortalDateOnly(item.patientDob)}
                    </Text>
                  </Group>
                  <Group justify="space-between" gap="xs">
                    <Text size="xs" c="dimmed">
                      Date of Service
                    </Text>
                    <Text size="xs" fw={500}>
                      {formatPortalDateOnly(item.dateOfService)}
                    </Text>
                  </Group>
                  <Group justify="space-between" gap="xs">
                    <Text size="xs" c="dimmed">
                      Report Date
                    </Text>
                    <Text size="xs" fw={500}>
                      {item.reportAvailable
                        ? formatPortalReportInstant(item.reportDate)
                        : "—"}
                    </Text>
                  </Group>
                </Stack>

                {item.reportAvailable && (
                  <Group justify="flex-end" gap="sm">
                    <Tooltip label="Download Report PDF" position="top">
                      <ActionIcon
                        variant="light"
                        color="gcpBlue.6"
                        size={44}
                        onClick={handleDownloadClick}
                        data-id={item.caseId}
                        aria-label="Download PDF"
                      >
                        <IconDownload size={22} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Print Report PDF" position="top">
                      <ActionIcon
                        variant="light"
                        color="gcpBlue.6"
                        size={44}
                        onClick={handlePrintClick}
                        data-id={item.caseId}
                        aria-label="Print PDF"
                      >
                        <IconPrinter size={22} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                )}
              </Card>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}

export default PortalReportTable;
